import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import {
  ICreateEmployeeRequest,
  IEditEmployee,
  IEmployeeSearchResponse,
  SearchEmployersParams,
} from '../models/';
import { IEmployeeDreamixBirthdayModel } from '../models/employee-dreamix-birthday.model';
import {
  IEmploymentHistoryRecord,
  IEmploymentHistoryRequest,
} from '../models/employment-history.model';
import { IPagination } from '../models/pagination.model';
import { ProjectHistoryRequest } from '../models/project-history.model';
import { AlertService, AlertType } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class AdminEmployeeService {
  private alertService = inject(AlertService);
  private httpClient = inject(HttpClient);
  private injector = inject(Injector);

  private readonly adminUrlBase = 'api/employees/admin';
  private readonly utilizationUrlBase = 'api/utilization';

  private _adminEmployees = signal<IPagination<IEmployeeSearchResponse>>(undefined);
  readonly adminEmployees = this._adminEmployees.asReadonly();

  private _employeeDaysLeft = signal<any>(undefined);
  readonly employeeDaysLeft = this._employeeDaysLeft.asReadonly();

  searchEmployees({
    params,
    payload,
    showAll = false,
  }: SearchEmployersParams & { showAll?: boolean }): Observable<
    IPagination<IEmployeeSearchResponse>
  > {
    let httpParams = params || new HttpParams();
    if (showAll) {
      httpParams = httpParams.set('showAll', 'true');
    }
    return this.httpClient.post<IPagination<IEmployeeSearchResponse>>(
      `${this.adminUrlBase}/search`,
      payload,
      { params: httpParams },
    );
  }

  fetchEmployees({
    params,
    payload,
    showAll = false,
  }: SearchEmployersParams & { showAll?: boolean }) {
    const responseSignal = toSignal(this.searchEmployees({ params, payload, showAll }), {
      injector: this.injector,
    });

    effect(
      () => {
        const data = responseSignal();
        if (!data) {
          this._adminEmployees.set(undefined);
          return;
        }
        try {
          this._adminEmployees.set(data);
        } catch (error) {
          this.alertService.showAlert(error, AlertType.error);
        }
      },
      { injector: this.injector, allowSignalWrites: true },
    );
  }

  fetchEmployeeDaysLeft({ employeeId, fromDate }: { employeeId: number; fromDate: string }) {
    const url = `${this.adminUrlBase}/vacation/${employeeId}`;
    const params = new HttpParams().set('fromDate', fromDate);

    const responseSignal = toSignal(this.httpClient.get<any>(url, { params }), {
      injector: this.injector,
    });

    effect(
      () => {
        const data = responseSignal();
        if (!data) {
          this._employeeDaysLeft.set(undefined);
          return;
        }
        try {
          this._employeeDaysLeft.set(data);
        } catch (error) {
          this.alertService.showAlert(error, AlertType.error);
        }
      },
      { injector: this.injector, allowSignalWrites: true },
    );
  }

  addEmployee(employee: ICreateEmployeeRequest): Observable<IEditEmployee> {
    return this.httpClient.post<IEditEmployee>(`${this.adminUrlBase}/create`, employee);
  }

  editEmployeeByAdmin(employee: IEditEmployee): Observable<IEditEmployee> {
    return this.httpClient.put<IEditEmployee>(`${this.adminUrlBase}/edit`, employee);
  }

  getEditEmployeeById(id: number): Observable<IEditEmployee> {
    return this.httpClient.get<IEditEmployee>(`${this.adminUrlBase}/edit/${id}`);
  }

  deleteEmployeeById(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.adminUrlBase}/${id}`);
  }

  uploadImg(id: number, file: File): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(`${this.adminUrlBase}/${id}/photo`, formData);
  }

  deleteImg(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.adminUrlBase}/${id}/photo`);
  }

  fetchExcelReport(path: string): Observable<{ buffer: Blob; fileName: string }> {
    const url = `${this.adminUrlBase}/${path}`;

    return this.httpClient
      .get<Blob>(url, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream',
        }),
        observe: 'response',
        responseType: 'arraybuffer' as 'json',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          buffer: response.body,
          fileName: this.fileNameFrom(response),
        })),
      );
  }

  getCombinedUtilizationExport(
    fromDate: string,
    toDate: string,
  ): Observable<{ buffer: Blob; fileName: string }> {
    const params = new HttpParams().set('fromDate', fromDate).set('toDate', toDate);
    return this.httpClient
      .get<Blob>(`${this.utilizationUrlBase}/combined`, {
        params,
        observe: 'response',
        responseType: 'arraybuffer' as 'json',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          buffer: response.body,
          fileName: this.fileNameFrom(response),
        })),
      );
  }

  private fileNameFrom(response: HttpResponse<Blob>): string {
    const header = response.headers.get('Content-Disposition');
    return header.split(';')[1].split('=')[1].replaceAll('"', '');
  }

  getAllEmployeesBirthdays(): Observable<IEmployeeDreamixBirthdayModel[]> {
    return this.httpClient.get<IEmployeeDreamixBirthdayModel[]>(`${this.adminUrlBase}/dreamixBd`);
  }

  saveEmployeeProjectHistory(
    employeeId: number,
    projectHistory: ProjectHistoryRequest[],
  ): Observable<ProjectHistoryRequest[]> {
    return this.httpClient.post<ProjectHistoryRequest[]>(
      `${this.adminUrlBase}/${employeeId}/projects-history`,
      projectHistory,
    );
  }

  getUtilizationRatesExport(
    fromDate: string,
    toDate: string,
    employeeId: string | null,
  ): Observable<any> {
    let params = new HttpParams().set('fromDate', fromDate).set('toDate', toDate);
    if (employeeId != null) {
      params = params.set('employeeId', employeeId);
    }
    return this.httpClient.get(`${this.adminUrlBase}/export`, {
      params,
      responseType: 'arraybuffer',
    });
  }

  getEmploymentHistory(employeeId: number): Observable<IEmploymentHistoryRecord[]> {
    return this.httpClient.get<IEmploymentHistoryRecord[]>(
      `${this.adminUrlBase}/${employeeId}/employment-history`,
    );
  }

  createEmploymentHistoryRecord(
    employeeId: number,
    request: IEmploymentHistoryRequest,
  ): Observable<IEmploymentHistoryRecord> {
    return this.httpClient.post<IEmploymentHistoryRecord>(
      `${this.adminUrlBase}/${employeeId}/employment-history`,
      request,
    );
  }

  updateEmploymentHistoryRecord(
    employeeId: number,
    changeId: number,
    request: IEmploymentHistoryRequest,
  ): Observable<IEmploymentHistoryRecord> {
    return this.httpClient.put<IEmploymentHistoryRecord>(
      `${this.adminUrlBase}/${employeeId}/employment-history/${changeId}`,
      request,
    );
  }

  deleteEmploymentHistoryRecord(employeeId: number, changeId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.adminUrlBase}/${employeeId}/employment-history/${changeId}`,
    );
  }
}
